import React from "react";
import { connect } from "react-redux";
import { formValueSelector, reduxForm, Field, change } from "redux-form";
import { FormattedDate, FormattedNumber } from "react-intl";
import { calculateLeasingTotalSum, generateLeasingPayments } from "../businessLogic";
import * as agent from "../agent";
import { normalizeMoney } from "../utility";
import PickRatesInput from "./PickRatesInput";
import { MaterialTextField, MaterialCheckbox } from "./MaterialInput";
import MaterialSlider from './MaterialSlider';
import PrintLink from './PrintLink';
import PrimaryDivider from './PrimaryDivider';
import Fade from '@material-ui/core/Fade';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableFooter from '@material-ui/core/TableFooter';
import TableRow from '@material-ui/core/TableRow';

const loanCalculatorForm = ({
    inputSum,
    inputProgram,
    inputRates = [],
    inputDuration,
    inputPreferentialRepayment,
    inputIsLeasingPrepayed,
    inputPreferentialPeriod,
    isLeasing,
    totalSum,
    dispatch,
    payments }) => (
    <form>
        <Grid container justify="center" spacing={16}>
            <Grid item className="common-column common-form">
                <Field name="programType"
                       select
                       component={MaterialTextField}
                       label="Программа займа" >
                    <MenuItem value="PTS_LEASING">ПТС Лизинг</MenuItem>
                    <MenuItem value="HOLD_LT">Стоянка ЗБ</MenuItem>
                </Field>
                <Field name="sum"
                       component={MaterialTextField}
                       normalize={normalizeMoney}
                       endAdornment="₽"
                       label="Сумма займа" />
                <Field component={MaterialTextField}
                       name="duration"
                       onChange={
                           (e) => {
                               const p = +e.target.value;
                               const iPP = +inputPreferentialPeriod;
                               dispatch(change("loan-calculator-form", "preferentialPeriod", p > iPP - 1 ? iPP : Math.round(p / 2)));
                           }
                       }
                       select
                       label="Срок займа" >
                    {isLeasing && <MenuItem value="3">3 мес.</MenuItem>}
                    {isLeasing && <MenuItem value="6">6 мес.</MenuItem>}
                    {isLeasing && Array(3).fill(0).map((_, i) => <MenuItem key={i} value={`${(i+1)* 12}`}>{(i+1)* 12} мес.</MenuItem>) }
                    {inputProgram === "HOLD_LT" && <MenuItem value="30">30 дн.</MenuItem> }
                </Field>
                <Field component={PickRatesInput}
                       name="rates"
                       duration={1}
                       sum={inputSum} />
                <PrintLink href={agent.src.printCalculatedLoan({
                    sum: inputSum,
                    program: inputProgram,
                    duration: inputDuration,
                    rates: inputRates,
                    preferentialRepayment: inputPreferentialRepayment,
                    isLeasingPrepayed: inputIsLeasingPrepayed,
                    preferentialPeriod: inputPreferentialPeriod })}
                           disabled={inputRates.length === 0 || !inputSum || !inputProgram || !inputDuration}
                           label="Распечатать" />
            </Grid>
            {isLeasing &&
                inputDuration > 0 && inputRates.length > 0 &&
                    <Grid item className="common-form ultrawide-column">
                        <Typography variant="h5" align="center">График платежей:</Typography>
                        <FormControlLabel
                             control={
                                 <Field name="isLeasingPrepayed"
                                        component={MaterialCheckbox} />}
                             label="Авансовый платеж"/>
                        { isLeasing &&
                            <FormControlLabel
                             control={
                                 <Field name="preferentialRepayment"
                                        component={MaterialCheckbox} />}
                             label="Льготное погашение"/>
                        }
                        { isLeasing && inputPreferentialRepayment &&
                            <Field component={MaterialSlider}
                                   name="preferentialPeriod"
                                   max={inputDuration - 1}/>
                        }
                        <Table padding="checkbox">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Дата платежа</TableCell>
                                    <TableCell>Сумма лизингового платежа за текущий период</TableCell>
                                    <TableCell>Размер выкупной цены в лизинговом платеже</TableCell>
                                    <TableCell>Сумма досрочного выкупа ТС, без учета очередного лизингового платежа</TableCell>
                                </TableRow >
                            </TableHead>
                            <TableBody>
                                {payments.map(({ paymentDate, paymentTotal, paymentCar, paymentBuyout }, i) =>
                                    <TableRow key={i}>
                                        <TableCell>
                                            <FormattedDate value={paymentDate}
                                                           day="numeric"
                                                           month="numeric"
                                                           year="numeric" />
                                        </TableCell>
                                        <TableCell>
                                            <FormattedNumber value={paymentTotal}
                                                             style={`currency`}
                                                             currency="RUB"
                                                             minimumFractionDigits={0} />
                                        </TableCell>
                                        <TableCell>
                                            <FormattedNumber value={paymentCar}
                                                             style={`currency`}
                                                             currency="RUB"
                                                             minimumFractionDigits={0} />
                                        </TableCell>
                                        <TableCell>
                                            <FormattedNumber value={paymentBuyout}
                                                             style={`currency`}
                                                             currency="RUB"
                                                             minimumFractionDigits={0} />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableCell>Итого: </TableCell>
                                    <TableCell>
                                        <FormattedNumber value={totalSum}
                                                         style={`currency`}
                                                         currency="RUB"
                                                         minimumFractionDigits={0} />
                                    </TableCell>
                                    <TableCell>
                                        <FormattedNumber value={inputSum}
                                                         style={`currency`}
                                                         currency="RUB"
                                                         minimumFractionDigits={0} />
                                    </TableCell>
                                    <TableCell>
                                        &nbsp;
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </Grid>
            }
        </Grid>
    </form>
);


const LoanCalculatorForm = connect(
    state => {
        const inputSum = +`${formValueSelector("loan-calculator-form")(state, "sum")}`.replace(/\s/g,``);
        const inputProgram = formValueSelector("loan-calculator-form")(state, "programType");
        const inputDuration = formValueSelector("loan-calculator-form")(state, "duration");
        const inputRates = formValueSelector("loan-calculator-form")(state, "rates");
        const inputPreferentialPeriod = formValueSelector("loan-calculator-form")(state, "preferentialPeriod");
        const inputIsLeasingPrepayed = formValueSelector("loan-calculator-form")(state, "isLeasingPrepayed");
        const inputPreferentialRepayment = formValueSelector("loan-calculator-form")(state, "preferentialRepayment");

        const isLeasing = inputProgram === "PTS_LEASING";
        const leasingRate = (inputRates || [0])[0];
        const duration = +inputDuration || 0;
        const totalSum = calculateLeasingTotalSum(inputSum, inputDuration, leasingRate);

        const payments = generateLeasingPayments({
            date: new Date,
            duration,
            leasingRate,
            sum: inputSum,
            preferentialPeriod: inputPreferentialPeriod,
            isLeasingPrepayed: inputIsLeasingPrepayed,
            isPreferentialRepayment: inputPreferentialRepayment
        });

        return {
            inputSum,
            inputProgram,
            inputDuration,
            inputRates,
            payments,
            isLeasing,
            totalSum,
            inputPreferentialRepayment,
            inputIsLeasingPrepayed,
            inputPreferentialPeriod,
            initialValues: Object.assign({
                duration: 12,
                rates: []
            })
        };
    },
    null
)(reduxForm({ form: "loan-calculator-form" })(loanCalculatorForm));

class Calculator extends React.Component {

    render() {

        return (
            <Fade in={true}>
                <div style={{ overflow: "hidden" }}>
                    <Typography variant="h4">Калькулятор займа</Typography>
                    <PrimaryDivider />
                    <Paper className="common-paper mb20">
                        <LoanCalculatorForm />
                    </Paper>
                </div>
            </Fade>
        );
    }
}

export default Calculator;



// WEBPACK FOOTER //
// ./src/components/Calculator.js